import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { SimpleSnackbarContextProvider } from "scmp-app/components/common/snackbar/simple-snackbar-item/contexts";
import IconClock from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-clock.svg";
import IconUser from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-user.svg";
import Placeholder from "scmp-app/components/newsletter/newsletter-category-item-slideshow/placeholder.svg";
import type { ScmpNewsletterColumnist } from "scmp-app/components/newsletter/newsletter-columnist-slideshow/types";
import { useNewsletterLandingContext } from "scmp-app/pages/newsletters/contexts";
import type { newsletterColumnistItemAuthor$key } from "scmp-app/queries/__generated__/newsletterColumnistItemAuthor.graphql";
import { FollowButtonVariant } from "scmp-app/scmp-sdk";

import {
  Container,
  Content,
  ContentHeader,
  Description,
  Flag,
  Footer,
  ImageContainer,
  Name,
  NumberOfSignedUpUsers,
  PlaceholderContainer,
  Preview,
  StyledBaseImage,
  StyledSignUpButton,
  Summary,
} from "./styles";

type Props = {
  className?: string;
  data: ScmpNewsletterColumnist;
  reference: newsletterColumnistItemAuthor$key;
};

export const NewsletterColumnistItem: FunctionComponent<Props> = ({
  className,
  data,
  reference,
}) => {
  const author = useFragment(
    graphql`
      fragment newsletterColumnistItemAuthor on Author {
        ...entityFollowButtonBase
        ...entityLink
      }
    `,
    reference,
  );

  const { isEmbed } = useNewsletterLandingContext();

  const renderImage = useCallback(() => {
    if (notEmpty(data.thumbnail)) {
      return (
        <ImageContainer>
          <StyledBaseImage
            $aspectRatio="3/2"
            $objectFit="cover"
            alt={data.name ?? ""}
            src={data.thumbnail}
          />
          {notEmpty(data.shortDescription) && <Flag>{data.shortDescription}</Flag>}
        </ImageContainer>
      );
    }
    return (
      <PlaceholderContainer>
        <Placeholder />
      </PlaceholderContainer>
    );
  }, [data.name, data.shortDescription, data.thumbnail]);

  return (
    <Container className={className}>
      {renderImage()}

      <Content>
        <ContentHeader>
          {data.summary && (
            <Summary>
              <IconClock />
              {data.summary}
            </Summary>
          )}
          {data.numberOfSignedUpUsers && (
            <NumberOfSignedUpUsers>
              <IconUser />
              {data.numberOfSignedUpUsers}
            </NumberOfSignedUpUsers>
          )}
        </ContentHeader>
        {data.name && <Name>{data.name}</Name>}

        {data.description && <Description>{data.description}</Description>}
      </Content>
      <Footer>
        <SimpleSnackbarContextProvider enabled={false}>
          <StyledSignUpButton
            reference={author}
            source="newsletter_index"
            variant={FollowButtonVariant.NewsletterColumnist}
          />
        </SimpleSnackbarContextProvider>
        <Preview reference={author} shallow target={isEmbed ? "_blank" : undefined}>
          Preview
        </Preview>
      </Footer>
    </Container>
  );
};

NewsletterColumnistItem.displayName = "NewsletterColumnistItem";
